import { createSelector } from 'reselect';

import { H4 } from '@r1-frontend/ui-react/components/typography/heading';
import { Paragraph4 } from '@r1-frontend/ui-react/components/typography/paragraph';
import { TItemTypes, TOrderSummaryComponent, TOrderSummaryItem } from '@r1-frontend/ui-react/organisms/OrderSummary/types';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';
import { getPluralize } from '@r1-frontend/shared/helpers/pluralize';
import { monthPluralize } from '@r1-frontend/shared/helpers/pluralize/predifined';

import { Plus100Status } from '~/src/entities/speed-bonus/context/constants';
import { TInitialState } from '~/src/entities/speed-bonus/context/reducer';

const pluralizedMonth = getPluralize(monthPluralize);

interface IOrderSummary {
    title: string,
    total: number,
    items: TItemTypes[],
    hasInstallmentEquipment: boolean,
}

export const selectSummaryInfo = createSelector(
    [
        (state: TInitialState) => state.tariff,
        (state: TInitialState) => state.routers,
        (state: TInitialState) => state.connectionCost,
    ],
    (tariff, routers, connectionCost): IOrderSummary | undefined => {
        if (typeof connectionCost === 'undefined' || !tariff) {
            return;
        }

        const selectedRouter = routers.reduce<{ items: TOrderSummaryItem[], cost: number }>(
            (acc, router) => {
                if (router.quantity === 0) {
                    return acc;
                }

                acc.cost += router.selectedPrice.cost * router.quantity;
                const countStr = router.quantity > 1 ? ` (${router.quantity} шт.)` : '';

                acc.items.push({
                    title: `${router.title}${countStr}`,
                    description: router.selectedPrice.duration
                        ? `Рассрочка на ${router.selectedPrice.duration} ${pluralizedMonth(router.selectedPrice.duration)}`
                        : 'Полная стоимость',
                    cost: router.selectedPrice.cost * router.quantity,
                    isMonthly: router.selectedPrice.duration > 0,
                    Component: Paragraph4,
                    color: COLORS.TextPrimary,
                });

                return acc;
            },
            { items: [], cost: 0 },
        );

        let totalCost = connectionCost + selectedRouter.cost;

        const summaryItems: (TOrderSummaryItem | TOrderSummaryComponent)[] = [
            ...selectedRouter.items,
            {
                title: 'Выезд техника',
                cost: connectionCost,
                isMonthly: false,
                Component: Paragraph4,
                color: COLORS.TextPrimary,
            },
        ];

        if (tariff.speedBonusStatus === Plus100Status) {
            totalCost += tariff.price;

            summaryItems.unshift({
                title: tariff.title,
                cost: tariff.price,
                isMonthly: true,
                Component: Paragraph4,
                color: COLORS.TextPrimary,
            });
        }

        const totalItem: TOrderSummaryItem = { title: 'Итого при подключении:', cost: totalCost, Component: H4 };

        return {
            total: totalCost,
            title: 'Ваша заявка',
            items: [summaryItems, 'separator', { total: totalItem }],
            hasInstallmentEquipment: !!selectedRouter.cost,
        };
    },
);
